'use client';

import styles from './Button.module.css';
import { useId, forwardRef } from 'react';
import { Icon, Flex, Flair, Text, Tooltip, Loading } from '@/_components';
import { getClasses } from '@/_utils/classnames';
import type { ButtonProps, ButtonSize } from './Button.types';
import type { IconSize } from '@/_types/scale';

const ICON_SIZE: Record<ButtonSize, IconSize> = {
  normal: 20,
  small: 20,
  tiny: 16,
};

const LOADING_SIZE: Record<ButtonSize, IconSize> = {
  normal: 20,
  small: 20,
  tiny: 16,
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      count,
      flair,
      iconLeft,
      iconRight,
      id,
      intent = 'secondary',
      isActive,
      isDisabled,
      isExpandable,
      isIconOnly,
      isLoading,
      isPulsing,
      isRounded,
      notificationCount,
      onClick,
      size = 'normal',
      tooltip,
      tooltipPlacement: placement,
      type = 'button',
      value,
      withTooltip,
      'data-testid': dataTestId,
    },
    ref,
  ) => {
    const defaultId = useId();

    const buttonClasses: string = getClasses(styles.button, {
      [className || '']: !!className,
      [styles.buttonClickable]: !!onClick,
      [styles.buttonDisabled]: isDisabled,
      [styles.buttonActive]: isActive,
      [styles.buttonExpandable]: isExpandable,
      [styles.buttonIconOnly]: isIconOnly,
      [styles.buttonNormal]: size === 'normal',
      [styles.buttonNormalCount]: size === 'normal' && !!count,
      [styles.buttonSmall]: size === 'small',
      [styles.buttonSmallCount]: size === 'small' && !!count,
      [styles.buttonTiny]: size === 'tiny',
      [styles.buttonTinyCount]: size === 'tiny' && !!count,
      [styles.buttonPrimary]: intent === 'primary',
      [styles.buttonSecondary]: intent === 'secondary',
      [styles.buttonGhost]: intent === 'ghost',
      [styles.buttonDanger]: intent === 'danger',
      [styles.buttonWarning]: intent === 'warning',
      [styles.buttonSuccess]: intent === 'success',
      [styles.buttonDangerSecondary]: intent === 'danger-secondary',
      [styles.buttonDangerGhost]: intent === 'danger-ghost',
      [styles.buttonWhite]: intent === 'white',
      [styles.buttonWhiteFill]: intent === 'white-fill',
      [styles.buttonPulsing]: isPulsing,
      [styles.buttonRounded]: isRounded,
    });

    const shouldShowIcon = !isLoading || (!!isIconOnly && !isLoading);

    const isDangerVariant = [
      'danger',
      'danger-secondary',
      'danger-ghost',
    ].includes(intent);

    return (
      <Tooltip
        isDisabled={!withTooltip}
        placement={placement}
        variant={
          isDangerVariant
            ? isDisabled
              ? 'button-danger-disabled'
              : 'button-danger'
            : isDisabled
              ? 'button-disabled'
              : 'button'
        }
      >
        <Tooltip.Trigger ref={ref}>
          <button
            className={buttonClasses}
            onClick={onClick}
            type={type}
            id={id || defaultId}
            disabled={isDisabled}
            data-testid={dataTestId}
          >
            {!!flair && (
              <Flair count={flair} className={styles.button__flair} />
            )}
            {shouldShowIcon && iconLeft && (
              <Icon size={ICON_SIZE[size]} name={iconLeft} />
            )}
            {!isIconOnly && value}
            {shouldShowIcon && iconRight && (
              <Icon size={ICON_SIZE[size]} name={iconRight} />
            )}
            {isLoading && <Loading size={LOADING_SIZE[size]} />}
            {!!count && (
              <Flex className={styles.button__count} isCentered={true}>
                {count}
              </Flex>
            )}
            {!!notificationCount && (
              <Flex
                className={styles.button__notificationCount}
                isCentered={true}
              >
                {notificationCount}
              </Flex>
            )}
          </button>
        </Tooltip.Trigger>
        <Tooltip.Content>
          <Text type="tiny" color="currentcolor" isEllipsis={false}>
            {tooltip || value}
          </Text>
        </Tooltip.Content>
      </Tooltip>
    );
  },
);

Button.displayName = 'Button';
